import { ReactNode } from 'react';
import { useEffectiveLevel } from '@/shared/permissions/hooks';
import type { PermLevel } from '@/shared/permissions/catalog';

const RANK: Record<PermLevel, number> = { hidden: 0, read: 1, write: 2 };

type Props = {
  /** Catalog node path, e.g. 'receivables.invoices'. */
  node: string;
  /** Minimum level needed to render children. Default 'read'. */
  require?: PermLevel;
  /** Replacement UI when level is insufficient. Defaults to a centered 403. */
  fallback?: ReactNode;
  children: ReactNode;
};

/**
 * Route / page-level permission wrapper. Redundant with the backend
 * `requirePermission` middleware, but ensures the SPA never even renders
 * the protected page (no API calls fire, no half-rendered chrome).
 *
 * Usage in App.tsx:
 *   <Route path="ar-invoices" element={
 *     <PermissionGate node="receivables.invoices">
 *       <ArInvoicesPage />
 *     </PermissionGate>
 *   } />
 */
export function PermissionGate({ node, require = 'read', fallback, children }: Props) {
  const level = useEffectiveLevel(node);
  if (RANK[level] >= RANK[require]) return <>{children}</>;
  return (
    <>
      {fallback ?? (
        <div className="p-10 flex flex-col items-center justify-center text-center text-secondary">
          <div className="text-[0.875rem] font-semibold text-primary-text mb-1">Access denied</div>
          <div className="text-[0.75rem]">
            You don't have permission to view this page.
          </div>
          <div className="text-meta text-muted mt-2 font-numeric">
            {node} · requires {require}
          </div>
        </div>
      )}
    </>
  );
}
